import {
  Promotion,
  PromotionCreateRequest,
  PromotionList,
  PromotionPublishRequest,
  PromotionResumeRequest,
  PromotionUpdateRequest,
} from '../stores/types/promotions.interface';
import { API_KEYS, Api } from './base.api';

export interface FetchPromotionsParams {
  companyId?: string;
  limit?: number;
  offset?: number;
  'filter_like[name]'?: string; // search
  'filter_in[status]'?: string; // status
}

export const promotionApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    fetchPromotions: builder.query<PromotionList, FetchPromotionsParams>({
      query: (params) => ({
        url: `/internal/core/v2/${API_KEYS.PROMOTIONS}`,
        method: 'GET',
        params: params,
      }),
      providesTags: () => [{ type: API_KEYS.PROMOTIONS, id: 'LIST' }],
    }),
    fetchPromotion: builder.query<Promotion, { promotionId: string }>({
      query: ({ promotionId }) => ({
        url: `/internal/core/v2/${API_KEYS.PROMOTIONS}/${promotionId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [{ type: API_KEYS.PROMOTIONS, id: arg.promotionId }],
    }),
    createPromotion: builder.mutation<Promotion, PromotionCreateRequest>({
      query: (payload) => ({
        url: `/internal/core/v2/${API_KEYS.PROMOTIONS}`,
        method: 'POST',
        body: payload,
        toast: {
          successTrans: 'promotion_create_success_toast',
          failedTrans: 'promotion_create_failed_toast',
          errorMessageMap: {
            'Promotion name already exists': 'promotion_duplicate_name_error_toast',
          },
        },
      }),
      invalidatesTags: () => [{ type: API_KEYS.PROMOTIONS, id: 'LIST' }],
    }),
    updatePromotion: builder.mutation<Promotion, { promotionId: string } & PromotionUpdateRequest>({
      query: ({ promotionId, ...payload }) => ({
        url: `/internal/core/v2/${API_KEYS.PROMOTIONS}/${promotionId}`,
        method: 'PATCH',
        body: payload,
        toast: {
          successTrans: 'promotion_update_success_toast',
          failedTrans: 'promotion_update_failed_toast',
          errorMessageMap: {
            'Promotion name already exists': 'promotion_duplicate_name_error_toast',
          },
        },
      }),
      invalidatesTags: (_result, _error, { promotionId }) => [
        { type: API_KEYS.PROMOTIONS, id: 'LIST' },
        { type: API_KEYS.PROMOTIONS, id: promotionId },
      ],
    }),
    publishPromotion: builder.mutation<
      Promotion,
      { promotionId: string } & PromotionPublishRequest
    >({
      query: ({ promotionId, ...payload }) => ({
        url: `/internal/core/v2/${API_KEYS.PROMOTIONS}/${promotionId}/publish`,
        method: 'POST',
        body: payload,
        toast: {
          successTrans: 'promotion_publish_success_toast',
          failedTrans: 'promotion_publish_failed_toast',
        },
      }),
      // Publishing cascades to the promotion's codes, so refresh the codes list too.
      invalidatesTags: (_result, _error, { promotionId }) => [
        { type: API_KEYS.PROMOTIONS, id: 'LIST' },
        { type: API_KEYS.PROMOTIONS, id: promotionId },
        { type: API_KEYS.PROMO_CODES, id: 'LIST' },
      ],
    }),
    pausePromotion: builder.mutation<Promotion, { promotionId: string }>({
      query: ({ promotionId }) => ({
        url: `/internal/core/v2/${API_KEYS.PROMOTIONS}/${promotionId}/pause`,
        method: 'POST',
        toast: {
          successTrans: 'promotion_pause_success_toast',
          failedTrans: 'promotion_pause_failed_toast',
        },
      }),
      // Pausing a promotion system-pauses its codes, so refresh the codes list too.
      invalidatesTags: (_result, _error, { promotionId }) => [
        { type: API_KEYS.PROMOTIONS, id: 'LIST' },
        { type: API_KEYS.PROMOTIONS, id: promotionId },
        { type: API_KEYS.PROMO_CODES, id: 'LIST' },
      ],
    }),
    resumePromotion: builder.mutation<Promotion, { promotionId: string } & PromotionResumeRequest>({
      query: ({ promotionId, ...payload }) => ({
        url: `/internal/core/v2/${API_KEYS.PROMOTIONS}/${promotionId}/resume`,
        method: 'POST',
        body: payload,
        toast: {
          successTrans: 'promotion_resume_success_toast',
          failedTrans: 'promotion_resume_failed_toast',
        },
      }),
      // Resuming a promotion can reactivate its codes, so refresh the codes list too.
      invalidatesTags: (_result, _error, { promotionId }) => [
        { type: API_KEYS.PROMOTIONS, id: 'LIST' },
        { type: API_KEYS.PROMOTIONS, id: promotionId },
        { type: API_KEYS.PROMO_CODES, id: 'LIST' },
      ],
    }),
    deletePromotion: builder.mutation<void, { promotionId: string }>({
      query: ({ promotionId }) => ({
        url: `/internal/core/v2/${API_KEYS.PROMOTIONS}/${promotionId}`,
        method: 'DELETE',
        toast: {
          successTrans: 'promotion_delete_success_toast',
          failedTrans: 'promotion_delete_failed_toast',
        },
      }),
      // Deleting/archiving a promotion removes/soft-deletes its codes too.
      invalidatesTags: () => [
        { type: API_KEYS.PROMOTIONS, id: 'LIST' },
        { type: API_KEYS.PROMO_CODES, id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useFetchPromotionsQuery,
  useLazyFetchPromotionsQuery,
  useFetchPromotionQuery,
  useCreatePromotionMutation,
  useUpdatePromotionMutation,
  usePublishPromotionMutation,
  usePausePromotionMutation,
  useResumePromotionMutation,
  useDeletePromotionMutation,
} = promotionApi;
